import { produce } from "immer";
import { setAgGridSort, setAgGridFloatingFilter } from "../../common/helpers/agGrid";
import {
    retainTableConfigByModule,
    validPersistedTypes,
    retainTerms as rt,
} from "../../common/components/table/agGrid/retainedSettings";
import { modules } from "../../constants";

export const tableRetainActions = (set, get) => ({
    getFloatingFilterInputByColId: () => {
        const activeElement = document.activeElement;
        const floatingFilters = document.querySelectorAll('.ag-floating-filter');
        const activeFloatingFilter = Array.from(floatingFilters).find(
            floatingFilter => floatingFilter.contains(activeElement)
        );
        return activeFloatingFilter?.querySelector('input') || null;
    },
    getShouldRetainTableByType: ({tableId, type}) => {
        if (!validPersistedTypes.includes(type)) return null;
        const selectedModule = get().selectedModule;
        return {
            selectedModule,
            shouldRetain: retainTableConfigByModule[selectedModule]?.[type]?.includes(tableId)
        }
    },
    setRetainedFloatingFilterState: ({tableId, colName, value}) => {
        const selectedModule = get().selectedModule;
        const editedInput = get().getFloatingFilterInputByColId();
        set(state =>
            produce(state, draft => {
                const prev = draft[selectedModule]?.retainedFloatingFilter?.[tableId]?.[colName];
                if (prev === value) return;
                if (!draft[selectedModule]) draft[selectedModule] = {};
                if (!draft[selectedModule].retainedFloatingFilter) draft[selectedModule].retainedFloatingFilter = {};
                if (!draft[selectedModule].retainedFloatingFilter[tableId]) draft[selectedModule].retainedFloatingFilter[tableId] = {};
                draft[selectedModule].retainedFloatingFilter[tableId][colName] = value;
                if (editedInput?.focus) setTimeout(() => editedInput.focus(), 10); // workaround to avoid focus loss.
            })
        );
    },
    setRetainedSortState: ({tableId, sortState}) => {
        const selectedModule = get().selectedModule;
        set(state => {
            const prev = state[selectedModule]?.retainedSortStateByView?.[tableId];
            if (prev && prev.colId === sortState.colId && prev.sort === sortState.sort) return state;
            return {
                [selectedModule]: {
                    ...state[selectedModule],
                    retainedSortStateByView: {
                        ...state[selectedModule]?.retainedSortStateByView,
                        [tableId]: sortState
                    }
                }
            };
        });
    },
    setTableRetainedStateByType: ({tableId, type, sortState, colName, value}) => {
        const { shouldRetain } = get().getShouldRetainTableByType({tableId, type});
        if (!shouldRetain) return;
        if (type === rt.sort) get().setRetainedSortState({ tableId, sortState });
        if (type === rt.floatingFilter) get().setRetainedFloatingFilterState({tableId, colName, value});
    },
    getTableRetainedStateByType: ({tableId, type}) => {
        const { selectedModule, shouldRetain } = get().getShouldRetainTableByType({tableId, type});
        if (!shouldRetain) return null;
        if (type === rt.sort) return get()[selectedModule]?.retainedSortStateByView?.[tableId] || null;
        if (type === rt.floatingFilter) return get()[selectedModule]?.retainedFloatingFilter?.[tableId] || null;
    },
    restoreTableRetainedStateByType: ({api, tableId, type}) => {
        const storedState = get().getTableRetainedStateByType({tableId, type});
        if (!storedState) return;
        if (type === rt.sort) setAgGridSort({api, sortState: storedState});
        if (type === rt.floatingFilter) setAgGridFloatingFilter({api, filterState: storedState});
    },
    handleFloatingFilterRetain: ({ tableViewId, api, setTableRetainedStateByType }) => {
        const selectedModule = get().selectedModule;
        if (retainTableConfigByModule[selectedModule]?.floatingFilter?.includes(tableViewId)) {
            const filterModel = api.getFilterModel();
            const savedFilters = get()[selectedModule]?.retainedFloatingFilter?.[tableViewId] || {};

            Object.keys(savedFilters).forEach(colName => {
                const currentFilter = filterModel[colName];
                if (!currentFilter || !currentFilter.filter) {
                    setTableRetainedStateByType({ tableId: tableViewId, type: rt.floatingFilter, colName, value: '' });
                }
            });

            Object.entries(filterModel).forEach(([colName, filterConfig]) => {
                if (filterConfig.filter) {
                    setTableRetainedStateByType({ tableId: tableViewId, type: rt.floatingFilter, colName, value: filterConfig.filter });
                }
            });
        }
    },
    setCrossSortOrder: (rowData, sourceTableId, sortedColId = null, sortDirection = null) => set(produce((state) => {
        const selectedModule = get().selectedModule;

        const sortOrder = {};
        rowData.forEach((agent, index) => {
            sortOrder[agent.agentId] = index;
        });

        state[selectedModule].crossSortOrder = sortOrder;
        state[selectedModule].crossSortSourceTableId = sourceTableId;
        state[selectedModule].crossSortColId = sortedColId;
        state[selectedModule].crossSortDirection = sortDirection;
    }), false, 'setCrossSortOrder'),
    setSelectedAgentIds: (agentIds) => set(produce((state) => {
        const selectedModule = get().selectedModule;
        if (selectedModule === modules.proficiencyMetrics.suggestedAgents) {
            state[selectedModule].selectedAgentIdsByTab[state[selectedModule].currentTabId] = agentIds;
        } else {
            state[selectedModule].selectedAgentIds = agentIds;
        }
    }), false, 'setSelectedAgentIds'),
    clearSelectedAgentIds: () => set(produce((state) => {
        const selectedModule = get().selectedModule;
        if (selectedModule === modules.proficiencyMetrics.suggestedAgents &&
                state[selectedModule]?.currentTabId) 
        {
            state[selectedModule].selectedAgentIdsByTab[state[selectedModule].currentTabId] = [];
        } else {
            state[selectedModule].selectedAgentIds = [];
        }
    }), false, 'clearSelectedAgentIds'),
    sortByStoredOrder: (agentData) => {
        const selectedModule = get().selectedModule;
        const crossSortOrder = get()[selectedModule]?.crossSortOrder;

        if (!agentData) return [];

        if (!crossSortOrder
            || !Object.keys(crossSortOrder).length
            || Object.keys(agentData).length === 0
        ) {
            return agentData;
        }

        return [...agentData].sort((a, b) => {
            const positionA = crossSortOrder[a.agentId] ?? Infinity;
            const positionB = crossSortOrder[b.agentId] ?? Infinity;
            return positionA - positionB;
        });
    }
});
